import React, {useState} from 'react';
import PropTypes from 'prop-types';
import withTracking from 'lib/hocs/withTracking';
import {getNodeText} from 'lib/reactUtils';
import constants from 'client/constants';
import {history} from 'client/history';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import {
  Dropdown,
  DropdownItem,
  Button,
  Icon,
  copyToClipboard,
  addToast,
  Modal,
  Dialogue,
  Text
} from '@albert-io/atomic';

const TrackedButton = withTracking(Button, {
  onClick: {
    feature: 'assessment list',
    event: 'click',
    type: 'dropdown',
    component: 'table',
    content: 'Options'
  }
});

const TrackedDropdownItem = withTracking(DropdownItem, {
  onClick: {
    feature: 'assessment list',
    event: 'click',
    type: 'dropdown item',
    component: 'options_menu',
    content: (e, props, ref) => getNodeText(ref)
  }
});

const ConfirmDeleteModal = ({exam, toggleModal, onConfirmDelete}) => {
  return (
    <Modal ariaLabel='Confirm deletion' handleClose={toggleModal}>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <Dialogue
            inModal
            hideCloseBtn
            size='s'
            handleClose={toggleModal}
            className={modalContentStyle}
          >
            <Dialogue.Title stretchTitle align='center'>
              Are you sure?
            </Dialogue.Title>
            <Dialogue.Body align='center'>
              <Text>
                You are about to delete <br />
                <strong>{exam.getName()}</strong>. <br />
                This action can not be undone.
              </Text>
            </Dialogue.Body>
            <Dialogue.BtnGroup align='right'>
              <CloseButtonWrapper flex>
                <Button color='secondary'>Cancel</Button>
              </CloseButtonWrapper>
              <Button color='destructive' onClick={onConfirmDelete}>
                Delete
              </Button>
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

ConfirmDeleteModal.propTypes = {
  exam: PropTypes.string,
  toggleModal: PropTypes.func,
  onConfirmDelete: PropTypes.func
};

const TeacherPracticeExamOptionsDropdown = ({exam, onDelete, onEditRoute, classroomId}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const toggleModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const copyShareLink = () => {
    const shareLink = exam.getSections().isEmpty()
      ? `${constants.ALBERT_FULL_PATH}/assignment/${exam.getId()}`
      : `${constants.ALBERT_FULL_PATH}/assessments/${exam.getId()}`;
    copyToClipboard(shareLink, () => {
      addToast({
        type: 'success',
        title: 'Link Copied!',
        message: 'The link to this asessment has been copied to your clipboard.'
      });
    });
  };

  return (
    <>
      <Dropdown
        positionFixed
        trigger={
          <TrackedButton size='s'>
            Options
            <Icon icon='caret-down' className='u-mar-l_1' />
          </TrackedButton>
        }
        position='right-start'
      >
        <TrackedDropdownItem
          icon={['far', 'cog']}
          onClick={() => {
            history.pushState(null, onEditRoute);
          }}
        >
          Settings
        </TrackedDropdownItem>
        <TrackedDropdownItem
          icon={['far', 'file-alt']}
          disabled={!exam.getStudents()?.first()}
          onClick={() => {
            history.pushState(
              null,
              `/classes/${classroomId}/assignments/${exam.getId()}/student-detail/${exam
                .getStudents()
                .sortBy((student) => {
                  return student.getLastName().toUpperCase();
                })
                .first()
                .getId()}/view`
            );
          }}
        >
          View student answers
        </TrackedDropdownItem>
        <TrackedDropdownItem onClick={() => copyShareLink(exam)} icon='link'>
          Share
        </TrackedDropdownItem>
        <TrackedDropdownItem
          icon={['far', 'trash-alt']}
          className='u-color_red-500'
          textProps={{color: 'red'}}
          iconProps={{color: 'red'}}
          onClick={() => setShowDeleteModal(true)}
        >
          Delete
        </TrackedDropdownItem>
      </Dropdown>
      {showDeleteModal && (
        <ConfirmDeleteModal exam={exam} toggleModal={toggleModal} onConfirmDelete={onDelete} />
      )}
    </>
  );
};

TeacherPracticeExamOptionsDropdown.propTypes = {
  exam: PropTypes.instanceOf(AssignmentModelV3),
  onDelete: PropTypes.func,
  onEditRoute: PropTypes.string,
  classroomId: PropTypes.string
};

export default TeacherPracticeExamOptionsDropdown;
